import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import { describePostOpError } from '@/utils/postOp/errors';

// Same wording as the other token-gated screens (src/api/billing.js).
const NO_TOKEN_MESSAGE = "Couldn't confirm your sign-in. Reload the page and try again.";

export const POST_OP_TIMEOUT_MS = 30000;

/**
 * Post-op auto-send — the three calls of teamio-serverless-api/forms-service/post-op-api.js
 * (plan docs/plans/2026-10-15-post-op-auto-send-plan.md §3b):
 *
 *   PUT  /get-forms-reminder-settings   { siteID }        -> { settings, readiness, workflowOverlaps }   STAFF: Bearer token   (getPostOpContext)
 *   PUT  /save-forms-reminder-settings  { siteID, value } -> { settings }                                STAFF: Bearer token   (savePostOpSettings)
 *   POST /get-long-url/{urlID}          { urlID }         -> { ok: true }                                PUBLIC (a patient's tap) (postOpAcknowledge)
 *
 * THE URLS ARE SHARED WITH OLDER ENDPOINTS ON PURPOSE (2026-10-17): the dev API Gateway is at its 500-resource quota, and the
 * quota counts paths, not methods - so each call takes a METHOD its path's existing function does not use (the legacy
 * reminder-settings pair is POST, getLongUrl is GET). The backend function name, not the URL, is the endpoint's identity:
 * do not "tidy" these into POST, or the request lands on the legacy function.
 *
 * ⛔ The two staff calls REQUIRE `Authorization: Bearer <Firebase ID token>` whose siteID claim is the body siteID — a
 * request without one is a 401 before any database read. Exactly like billing.js, they FAIL CLOSED: with no token we do
 * not call at all. The acknowledge is deliberately public (the patient has no session) and sends no auth.
 *
 * Failures are rethrown as an Error (postOpApiError) carrying `.info` = describePostOpError(): { kind, status, code, message,
 * errors, reason } — `message` is the sentence to show (the server's own reason for a 409 ENABLE_BLOCKED, the field
 * problems for a 422 INVALID_SETTINGS).
 */

/** An Error whose `.info` is the describePostOpError() result (a plain function, not a subclass: no transpile surprises). */
export function postOpApiError(info) {
  const err = new Error(info.message);
  err.name = 'PostOpApiError';
  err.info = info;
  return err;
}

function noToken() {
  return postOpApiError({ kind: 'auth', status: null, code: 'NO_TOKEN', message: NO_TOKEN_MESSAGE, errors: [], reason: '' });
}

async function authHeaders(auth) {
  const idToken = await getIdToken(auth);
  if (!idToken) throw noToken();
  return { Authorization: `Bearer ${idToken}` };
}

export async function fetchPostOpContext({ auth, siteID } = {}) {
  const headers = await authHeaders(auth);
  try {
    const { data } = await axios.put('/get-forms-reminder-settings', { siteID }, { headers, timeout: POST_OP_TIMEOUT_MS, crossdomain: true });
    return data;
  } catch (err) {
    throw postOpApiError(describePostOpError(err, 'Couldn’t load the post-op settings. Please try again.'));
  }
}

/**
 * Save the message settings and/or the master switch. `value` may hold just the keys that changed
 * ({ enabled:'TRUE' } from the switch, the message fields from Save) — the server overlays them on the stored blob.
 * `liveSince` is stamped by the SERVER: render the returned `settings.liveSince`, never compute it.
 */
export async function savePostOpSettings({ auth, siteID, value } = {}) {
  const headers = await authHeaders(auth);
  try {
    const { data } = await axios.put('/save-forms-reminder-settings', { siteID, value }, { headers, timeout: POST_OP_TIMEOUT_MS, crossdomain: true });
    return data;
  } catch (err) {
    throw postOpApiError(describePostOpError(err, 'Couldn’t save the post-op settings. Please try again.'));
  }
}

/**
 * The patient's "I've read these instructions" tap on ONE form's link. Resolves true on { ok: true }; any failure throws an
 * Error carrying `.status` (undefined for a network failure) so the caller can choose "tap again" vs "call the office".
 * `urlID` is whatever ViewForm resolved the link with — the 7-char short code or the FormUrlID UUID. It goes in the PATH (the
 * server reads it from there) and is repeated in the body, where the server requires it to match.
 */
export async function acknowledgePostOp(urlID) {
  try {
    const { data } = await axios.post('/get-long-url/' + encodeURIComponent(urlID), { urlID }, { timeout: POST_OP_TIMEOUT_MS, crossdomain: true });
    if (!data || data.ok !== true) {
      const bad = new Error('Unexpected acknowledge response');
      bad.status = 502;
      throw bad;
    }
    return true;
  } catch (err) {
    if (err && err.status) throw err;
    const failure = new Error('Acknowledge failed');
    failure.status = err && err.response ? err.response.status : undefined;
    throw failure;
  }
}
